import { useJobProductionViews, ProductionJobList } from '../components/jobs/JobProductionViews';
import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  Box,
  Typography,
  Button,
  Select,
  Option,
  CircularProgress,
  Alert,
  Sheet,
  Checkbox,
} from '@mui/joy';
import { ArrowsClockwise as RefreshCw, Plus, Gear as Settings } from '@phosphor-icons/react';
import { useJobs } from '../hooks/useJobs';
import { useNavigate } from 'react-router-dom';
import type { Job } from '../types';
import { KanbanColumn } from '../components/KanbanColumn';
import { JobCard } from '../components/JobCard';
import { AddJobModal, EditJobModal, JobDropFooter } from '../components/jobs';
import { QuickActionsProvider } from '../components/global';

export const JobBoard: React.FC = () => {
  const navigate = useNavigate();
  const [showCompleted, setShowCompleted] = useState(false);

  const {
    jobs: allJobs,
    boards,
    statuses,
    loading,
    selectedBoard,
    setSelectedBoard,
    updateJobStatus,
    markJobComplete,
    deleteJob,
    refreshData,
  } = useJobs({ includeCompleted: showCompleted });

  const { jobs, toolbar, viewMode } = useJobProductionViews(allJobs);

  const [activeJob, setActiveJob] = useState<Job | null>(null);
  const [hoveredColumnId, setHoveredColumnId] = useState<number | null>(null);
  const [isAddJobModalOpen, setIsAddJobModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Register Quick Actions modal handler
  React.useEffect(() => {
    const registerJobModal = () => {
      if (window.QuickActions) {
        window.QuickActions.registerJobModal(() => {
          setIsAddJobModalOpen(true);
        });
        return true;
      }
      return false;
    };

    // Try to register immediately
    if (!registerJobModal()) {
      // If QuickActions API isn't ready, retry after a short delay
      const retryTimeout = setTimeout(() => {
        registerJobModal();
      }, 50);
      
      // Cleanup timeout if component unmounts
      return () => {
        clearTimeout(retryTimeout);
        if (window.QuickActions) {
          window.QuickActions.unregisterJobModal();
        }
      };
    }
    
    // Cleanup on unmount
    return () => {
      if (window.QuickActions) {
        window.QuickActions.unregisterJobModal();
      }
    };
  }, []);

  // Add keyboard navigation for horizontal scrolling
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!scrollContainerRef.current) return;
      
      const scrollAmount = 320; // Width of one column
      
      if (e.key === 'ArrowLeft' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'auto' });
          }
        });
      } else if (e.key === 'ArrowRight' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'auto' });
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Add horizontal scroll with mouse wheel (when holding shift)
  React.useEffect(() => {
    let rafId: number | null = null;
    let scrollTarget = 0;
    
    const smoothScroll = () => {
      if (!scrollContainerRef.current) return;
      
      const current = scrollContainerRef.current.scrollLeft;
      const diff = scrollTarget - current;
      
      if (Math.abs(diff) > 1) {
        scrollContainerRef.current.scrollLeft = current + diff * 0.15;
        rafId = requestAnimationFrame(smoothScroll);
      } else {
        scrollContainerRef.current.scrollLeft = scrollTarget;
        rafId = null;
      }
    };
    
    const handleWheel = (e: WheelEvent) => {
      if (!scrollContainerRef.current) return;
      
      // Horizontal scroll when shift is held or when scroll is horizontal
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        e.stopPropagation();
        
        const scrollAmount = e.shiftKey ? e.deltaY : e.deltaX;
        scrollTarget = scrollContainerRef.current.scrollLeft + scrollAmount;
        
        if (!rafId) {
          rafId = requestAnimationFrame(smoothScroll);
        }
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleWheel);
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
      };
    }
  }, []); // Remove statuses dependency to prevent unnecessary re-attachments

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const job = jobs.find(j => j.id === active.id);
    setActiveJob(job || null);
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    if (!over) { setHoveredColumnId(null); return; }
    const overId = String(over.id);
    if (overId.startsWith('column-')) {
      setHoveredColumnId(parseInt(overId.replace('column-', '')));
    } else {
      // Over a job card — look up its column
      const overJob = jobs.find(j => j.id === over.id);
      setHoveredColumnId(overJob?.board_status ?? null);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveJob(null);
    setHoveredColumnId(null);

    if (!over) return;

    const activeJobId = active.id as number;
    const overId = String(over.id); // String() ensures runtime conversion (over.id may be a number)
    const job = jobs.find(j => j.id === activeJobId);

    if (!job) return;

    // Check if dropped on Complete zone
    if (overId === 'job-complete') {
      if (window.confirm(`Mark job "${job.name || job.address_1}" as complete?`)) {
        try {
          // Mark the job as complete (stays in current stage, just hidden by default)
          await markJobComplete(activeJobId, true);
        } catch (error) {
          // TODO: Show error notification
          console.error('Failed to mark job as complete:', error);
        }
      }
      return;
    }

    // Check if dropped on Archive zone
    if (overId === 'job-archive') {
      if (window.confirm(`Archive job "${job.name || job.address_1}"? This will move it to an archived state.`)) {
        try {
          // Find an "Archive" or "Closed" status
          const archiveStatus = statuses.find(s =>
            s.name.toLowerCase().includes('archive') ||
            s.name.toLowerCase().includes('closed')
          );

          if (archiveStatus) {
            await updateJobStatus(activeJobId, archiveStatus.id);
          } else {
            alert('No archive status found. Please create an "Archive" or "Closed" status in board settings.');
          }
        } catch (error) {
          // TODO: Show error notification
          console.error('Failed to archive job:', error);
        }
      }
      return;
    }

    // Normal column drop - over.id is either 'column-{statusId}' or a job ID (number)
    let targetStatusId: number;

    if (overId.startsWith('column-')) {
      targetStatusId = parseInt(overId.replace('column-', ''));
    } else {
      // Dropped over another job card — find that job's column status
      const overJob = jobs.find(j => j.id === over.id);
      if (!overJob) return;
      targetStatusId = overJob.board_status;
    }

    if (targetStatusId === job.board_status) return;

    try {
      await updateJobStatus(activeJobId, targetStatusId);
    } catch (error) {
      // TODO: Show error notification
    }
  };

  const handleAddJob = (statusId: number) => {
    // TODO: Open job creation form
  };

  const handleEditJob = (job: Job) => {
    setEditingJob(job);
  };

  const handleUncompleteJob = async (jobId: number) => {
    const job = jobs.find((j) => j.id === jobId);
    const label = job?.name || job?.address_1 || job?.job_number || 'this job';
    if (!window.confirm(`Reopen "${label}" and return it to the active board?`)) {
      return;
    }
    try {
      await markJobComplete(jobId, false);
    } catch (error) {
      console.error('Failed to uncomplete job:', error);
    }
  };

  const handleDeleteJob = async (jobId: number) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await deleteJob(jobId);
      } catch (error) {
        // TODO: Show error notification
      }
    }
  };

  const getJobsByStatus = (statusId: number) => {
    return jobs.filter(job => job.board_status === statusId);
  };

  // Get the current board data
  const currentBoard = boards.find(b => b.id === selectedBoard);

  if (loading.isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-lg" textColor="text.secondary">
          Loading job board...
        </Typography>
      </Box>
    );
  }

  if (loading.error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Alert color="danger" size="lg">
          <Typography level="title-md">Error loading job board</Typography>
          <Typography level="body-sm">{loading.error}</Typography>
        </Alert>
        <Button
          onClick={refreshData}
          color="primary"
          variant="solid"
          startDecorator={<RefreshCw size={16} />}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <QuickActionsProvider 
      enableContactModal={true}
      onContactCreated={() => {
        // Could refresh contacts if needed
      }}
    >
      <Box sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 1.5, md: 3 },
        minHeight: '100vh'
      }}>
        {/* Header */}
        <Sheet
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 'lg',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography level="h2" component="h1">
                Job Board
              </Typography>
              {boards.length > 0 && (
                <>
                  <Select
                    value={selectedBoard?.toString() || ''}
                    onChange={(_, value) => {
                      if (value) {
                        const boardId = parseInt(value as string);
                        setSelectedBoard(boardId);
                      }
                    }}
                    placeholder="Select board"
                    size="sm"
                  >
                    {boards.map(board => (
                      <Option key={board.id} value={board.id.toString()}>
                        {board.name}
                      </Option>
                    ))}
                  </Select>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    onClick={() => navigate('/operations/jobs/settings')}
                    startDecorator={<Settings size={16} />}
                  >
                    Settings
                  </Button>
                  <Checkbox
                    label="Show Completed"
                    size="sm"
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                  />
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={refreshData}
                disabled={loading.isLoading}
                startDecorator={<RefreshCw size={16} className={loading.isLoading ? 'animate-spin' : ''} />}
              >
                Refresh
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={() => setIsAddJobModalOpen(true)}
                startDecorator={<Plus size={16} />}
              >
                Add Job
              </Button>
            </Box>
          </Box>
        </Sheet>

        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'lg' }}>{toolbar}</Sheet>
        {/* Kanban Board */}
        <Sheet
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: viewMode === 'list' ? 'auto' : 'hidden',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 'lg',
          }}
        >
          {viewMode === 'list' ? <ProductionJobList jobs={jobs} onOpen={job => navigate(`/operations/jobs/${job.id}`)} /> : statuses.length === 0 ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              flexDirection: 'column',
              gap: 2
            }}>
              <Typography level="h4" textColor="text.secondary">
                No board selected or no statuses available
              </Typography>
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={pointerWithin}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <Box sx={{
                flex: 1,
                overflow: 'hidden',
                position: 'relative',
                minHeight: 0 // Important for flex children with overflow
              }}>
                <div
                  className="kanban-scroll-container"
                  ref={scrollContainerRef}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflowX: 'auto',
                    overflowY: 'auto' // Enable vertical scrolling
                  }}
                >
                  <div
                    className="kanban-board"
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      padding: '1rem',
                      minHeight: 'calc(100vh - 300px)', // Ensure minimum height for the board
                      height: 'auto', // Allow board to expand
                      alignItems: 'stretch', // Stretch columns to same height
                      width: 'max-content'
                    }}
                  >
                    {statuses
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((status) => (
                        <div
                          key={status.id}
                          className="kanban-column-wrapper"
                          style={{
                            flex: '0 0 320px',
                            minWidth: '320px',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                          }}
                        >
                          <KanbanColumn
                            status={status}
                            jobs={getJobsByStatus(status.id)}
                            onAddJob={handleAddJob}
                            onEditJob={handleEditJob}
                            onDeleteJob={handleDeleteJob}
                            onUncompleteJob={handleUncompleteJob}
                            accentColor={currentBoard?.color || undefined}
                            isDropTarget={hoveredColumnId === status.id}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              </Box>

              <DragOverlay>
                {activeJob ? (
                  <JobCard job={activeJob} />
                ) : null}
              </DragOverlay>
              {/* Drop Footer - only show when dragging */}
              <JobDropFooter isActive={!!activeJob} />
            </DndContext>
          )}
        </Sheet>

        {/* Add Job Modal */}
        <AddJobModal
          open={isAddJobModalOpen}
          onClose={() => setIsAddJobModalOpen(false)}
          onSuccess={refreshData}
          selectedBoard={selectedBoard || undefined}
          selectedStatus={statuses.length > 0 ? statuses[0].id : undefined}
        />

        {/* Edit Job Modal */}
        <EditJobModal
          open={!!editingJob}
          onClose={() => setEditingJob(null)}
          job={editingJob as any}
          onSuccess={refreshData}
        />
      </Box>
    </QuickActionsProvider>
  );
};